import { type DefinitionsUpdate, connectSubscriptions } from "@norn/sdk"
import { useQueryClient } from "@tanstack/react-query"
import { type ReactNode, createContext, useContext, useEffect, useState } from "react"
import { type MessageKey, en } from "@/i18n/en"
import { keys } from "./api"
import type { Scope } from "./connection"
import { useCell } from "./session"

/** Where the definitions change stream stands for this scope. */
export type LiveStatus =
  | { readonly kind: "connecting" }
  | { readonly kind: "live"; readonly revision: number }
  /** The cell stopped the stream (authorization changed) or refused it; definitions are not live. */
  | { readonly kind: "stopped"; readonly reason: string; readonly reasonKey?: MessageKey }

export interface DefinitionsLive {
  readonly status: LiveStatus
  /** The latest installed revision the stream announced, and the artifacts it changed. */
  readonly last: { readonly revision: number; readonly changedArtifactIds: readonly string[] } | null
}

const LiveContext = createContext<DefinitionsLive>({ status: { kind: "connecting" }, last: null })

/**
 * Subscribe to the cell's `definitions` channel (#3223) for one scope and refetch the definitions when
 * an install lands or the stream asks for a resync. Refetching replaces only cached reads: editors keep
 * their unsaved edits and compare against the new revision themselves.
 */
export function DefinitionsLiveProvider({ scope: { orgId, environmentId }, children }: { scope: Scope; children: ReactNode }) {
  const { connection } = useCell()
  const queryClient = useQueryClient()
  const [live, setLive] = useState<DefinitionsLive>({ status: { kind: "connecting" }, last: null })

  useEffect(() => {
    const scope = { orgId, environmentId }
    const client = connectSubscriptions({
      baseUrl: connection.baseUrl,
      orgId,
      environmentId,
      ...(connection.token ? { token: connection.token } : {}),
      ...(connection.actor ? { actor: connection.actor } : {}),
    })
    const refetch = () => void queryClient.invalidateQueries({ queryKey: keys.defs(connection.baseUrl, scope).slice(0, 4) })
    const onEvent = (event: DefinitionsUpdate) => {
      if (event.kind === "closed") {
        const reasonKey = event.reason === "authorization_changed" ? "live.noRead" : "live.closed"
        setLive((l) => ({ ...l, status: { kind: "stopped", reason: en[reasonKey], reasonKey } }))
        return
      }
      setLive((l) => ({
        status: { kind: "live", revision: event.revision },
        last: event.kind === "installed" ? { revision: event.revision, changedArtifactIds: event.changedArtifactIds } : l.last,
      }))
      // `resumed` at a revision the cached read may not have, an install, or a resync: read the set again.
      if (event.kind !== "resumed" || cachedVersion() !== event.revision) refetch()
    }
    const cachedVersion = () => queryClient.getQueryData<{ version: number }>(keys.defs(connection.baseUrl, scope))?.version
    const handle = client.subscribeDefinitions({
      onEvent,
      onRefused: (refusal) => setLive((l) => ({ ...l, status: { kind: "stopped", reason: `${refusal.explanation} (${refusal.code})` } })),
    })
    return () => {
      handle.unsubscribe()
      client.close()
    }
  }, [connection.baseUrl, connection.token, connection.actor, orgId, environmentId, queryClient])

  return <LiveContext.Provider value={live}>{children}</LiveContext.Provider>
}

export function useDefinitionsLive(): DefinitionsLive {
  return useContext(LiveContext)
}
